import { useEffect, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useSelector } from "react-redux";
import { selectUser } from "features/userSlice";
import { auth, db } from "../firebase";
import {
  getMetaCheckoutBillingPeriod,
  hasMetaCheckoutIntent,
  isSchoolLicensedPremiumUser,
  trackSubscriptionSuccess,
} from "../utils/metaPixel";

function shouldTrackPremiumActivation({ wasPremium, isPremium, uid, checkoutIntent }) {
  if (!uid || !isPremium) {
    return false;
  }

  if (!wasPremium && isPremium) {
    return true;
  }

  return wasPremium === null && checkoutIntent;
}

function MetaPremiumTracker() {
  const user = useSelector(selectUser);
  const previousPremiumRef = useRef(null);
  const trackingInFlightRef = useRef(false);

  useEffect(() => {
    previousPremiumRef.current = null;
    trackingInFlightRef.current = false;
  }, [user?.uid]);

  useEffect(() => {
    const uid = user?.uid || auth.currentUser?.uid;
    if (!uid) {
      return undefined;
    }

    const userRef = doc(db, "usersNew", uid);

    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      const userData = snapshot.data();
      if (!userData) {
        return;
      }

      if (isSchoolLicensedPremiumUser(userData)) {
        previousPremiumRef.current = !!userData.premium;
        return;
      }

      const wasPremium = previousPremiumRef.current;
      const isPremium = !!userData.premium;
      const checkoutIntent = hasMetaCheckoutIntent(uid);
      const shouldTrack = shouldTrackPremiumActivation({
        wasPremium,
        isPremium,
        uid,
        checkoutIntent,
      });

      previousPremiumRef.current = isPremium;

      if (!shouldTrack || trackingInFlightRef.current) {
        return;
      }

      trackingInFlightRef.current = true;

      const tracking = userData.metaSubscriptionTracking || {};
      const billingPeriod = tracking.billingPeriod || getMetaCheckoutBillingPeriod();

      void trackSubscriptionSuccess({
        uid,
        email: userData.email || user?.email,
        firstName: userData.firstName || user?.firstName,
        lastName: userData.lastName || user?.lastName,
        billingPeriod,
        value: tracking.value,
        currency: tracking.currency,
        subscribeEventId: tracking.subscribeEventId,
        purchaseEventId: tracking.purchaseEventId,
      }).finally(() => {
        trackingInFlightRef.current = false;
      });
    });

    return () => unsubscribe();
  }, [user?.email, user?.firstName, user?.lastName, user?.uid]);

  return null;
}

export default MetaPremiumTracker;
